import { FC, useMemo, useState } from 'react';
import { Box, Typography, useTheme, Grid, Card, CardContent } from '@mui/material';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useResponsive } from 'src/hooks/useResponsiveHook';
import { useDashboardPeopleHook } from '../hooks/useDashboardPeopleHook';
import { useDashboardState } from '../contexts/dashboard.context';
import {
  ActionItems,
  SummaryCards,
  UpcomingItems,
  TimeRangePicker,
  SummaryCardData,
} from './shared';
import {
  IPeopleActionItem,
  IPeopleUpcomingItem,
  ITimeRangeParams,
} from '../../../../apis/dashboard/dashboard.interfaces';
import {
  School,
  People,
  TrendingUp,
  PersonAdd,
  EventBusy,
  CheckCircle,
} from '@mui/icons-material';
import { getDashboardNavigationPaths } from '../../../../apis/dashboard/dashboard.helpers';
import { useNavigate } from 'react-router-dom';

const DashboardPeopleTabView: FC = () => {
  const { translateText } = useTranslation();
  const theme = useTheme();
  const { isMobile } = useResponsive();
  const [timeRange, setTimeRange] = useState<ITimeRangeParams>({
    period: 'monthly',
  });

  // Use hook directly with time range params - React Query will refetch automatically
  const { data: peopleData, isLoading: isLoadingPeople } = useDashboardPeopleHook(timeRange);
  
  // Still need legacy data for summary cards (students, staff, attendance)
  const {
    studentsDashboardData,
    staffDashboardData,
    attendanceDashboardData,
    isLoading: isLoadingLegacy,
  } = useDashboardState();
  
  const isLoading = isLoadingPeople || isLoadingLegacy;

  const navigationPaths = getDashboardNavigationPaths();
  const navigate = useNavigate();

  // Prepare action items
  const actionItems = useMemo<IPeopleActionItem[]>(() => {
    if (!peopleData?.actionItems) {
      return [];
    }
    
    const items: IPeopleActionItem[] = [];
    
    // Attendance issues
    if (peopleData.actionItems.attendanceIssues?.items) {
      items.push(...peopleData.actionItems.attendanceIssues.items);
    }
    
    // Leave requests
    if (peopleData.actionItems.leaveRequests?.items) {
      items.push(...peopleData.actionItems.leaveRequests.items);
    }
    
    // Documentation gaps
    if (peopleData.actionItems.documentationGaps?.items) {
      items.push(...peopleData.actionItems.documentationGaps.items);
    }
    
    return items;
  }, [peopleData]);

  // Prepare upcoming items (milestones, meetings, reviews)
  const upcomingItems = useMemo<IPeopleUpcomingItem[]>(() => {
    if (!peopleData?.upcomingItems) {
      return [];
    }
    
    const items: IPeopleUpcomingItem[] = [];

    // Work anniversaries and birthdays
    if (peopleData.upcomingItems.milestones) {
      items.push(...peopleData.upcomingItems.milestones);
    }

    // Parent-teacher meetings
    if (peopleData.upcomingItems.meetings) {
      items.push(...peopleData.upcomingItems.meetings);
    }

    // Performance reviews and contract renewals
    if (peopleData.upcomingItems.reviews) {
      items.push(...peopleData.upcomingItems.reviews);
    }

    return items;
  }, [peopleData]);

  // Prepare summary cards - use new data structure when available, fallback to legacy
  const summaryCards = useMemo<SummaryCardData[]>(() => {
    const cards: SummaryCardData[] = [];

    // Use new data structure if available
    if (peopleData?.summary) {
      // Students summary from new structure
      if (peopleData.summary.students) {
        cards.push({
          id: 'total-students',
          title: translateText('dashboard.people.summary.totalStudents'),
          value: peopleData.summary.students.total || 0,
          icon: <School />,
          color: 'primary',
          navigationPath: navigationPaths.students.list,
          subtitle: `${peopleData.summary.students.active || 0} ${translateText('dashboard.people.summary.active')}`,
          isCritical: true, // Always show on mobile
        });

        cards.push({
          id: 'new-enrollments',
          title: translateText('dashboard.people.summary.newEnrollments'),
          value: peopleData.summary.students.newEnrollments || 0,
          icon: <PersonAdd />,
          color: 'success',
          navigationPath: navigationPaths.students.list,
        });

        cards.push({
          id: 'student-attendance-rate',
          title: translateText('dashboard.people.summary.studentAttendanceRate'),
          value: `${peopleData.summary.students.attendanceRate || 0}%`,
          icon: <TrendingUp />,
          color: 'info',
          navigationPath: navigationPaths.students.attendance,
        });
      }

      // Staff summary from new structure
      if (peopleData.summary.staff) {
        cards.push({
          id: 'total-staff',
          title: translateText('dashboard.people.summary.totalStaff'),
          value: peopleData.summary.staff.total || 0,
          icon: <People />,
          color: 'primary',
          navigationPath: navigationPaths.staff.list,
          subtitle: `${peopleData.summary.staff.active || 0} ${translateText('dashboard.people.summary.active')}`,
        });

        cards.push({
          id: 'staff-on-leave',
          title: translateText('dashboard.people.summary.staffOnLeave'),
          value: peopleData.summary.staff.onLeave || 0,
          icon: <EventBusy />,
          color: 'warning',
          navigationPath: navigationPaths.staff.list,
        });

        cards.push({
          id: 'staff-attendance-rate',
          title: translateText('dashboard.people.summary.staffAttendanceRate'),
          value: `${peopleData.summary.staff.attendanceRate || 0}%`,
          icon: <TrendingUp />,
          color: 'info',
          navigationPath: navigationPaths.staff.attendance,
        });
      }

      // Overall attendance from new structure
      if (peopleData.summary.attendance) {
        cards.push({
          id: 'overall-attendance-rate',
          title: translateText('dashboard.people.summary.overallAttendanceRate'),
          value: `${peopleData.summary.attendance.overallRate || 0}%`,
          icon: <CheckCircle />,
          color: 'success',
          navigationPath: navigationPaths.students.attendance,
          subtitle: `${peopleData.summary.attendance.totalPresent || 0} ${translateText('dashboard.people.summary.present')}`,
          isCritical: true, // Always show on mobile
        });
      }
    } else {
      // Fallback to legacy data structure for backward compatibility
      if (studentsDashboardData?.enrollment) {
        cards.push({
          id: 'total-students',
          title: translateText('dashboard.people.summary.totalStudents'),
          value: studentsDashboardData.enrollment.total || 0,
          icon: <School />,
          color: 'primary',
          navigationPath: navigationPaths.students.list,
          subtitle: `${studentsDashboardData.enrollment.active || 0} ${translateText('dashboard.people.summary.active')}`,
          isCritical: true,
        });

        cards.push({
          id: 'new-enrollments',
          title: translateText('dashboard.people.summary.newEnrollments'),
          value: studentsDashboardData.enrollment.newEnrollments || 0,
          icon: <PersonAdd />,
          color: 'success',
          navigationPath: navigationPaths.students.list,
        });

        if (studentsDashboardData.attendance?.summary) {
          cards.push({
            id: 'student-attendance-rate',
            title: translateText('dashboard.people.summary.studentAttendanceRate'),
            value: `${studentsDashboardData.attendance.summary.attendancePercentage || 0}%`,
            icon: <TrendingUp />,
            color: 'info',
            navigationPath: navigationPaths.students.attendance,
          });
        }
      }

      if (staffDashboardData?.summary) {
        cards.push({
          id: 'total-staff',
          title: translateText('dashboard.people.summary.totalStaff'),
          value: staffDashboardData.summary.totalCount || 0,
          icon: <People />,
          color: 'primary',
          navigationPath: navigationPaths.staff.list,
          subtitle: `${staffDashboardData.summary.activeCount || 0} ${translateText('dashboard.people.summary.active')}`,
        });

        cards.push({
          id: 'staff-on-leave',
          title: translateText('dashboard.people.summary.staffOnLeave'),
          value: staffDashboardData.summary.onLeaveCount || 0,
          icon: <EventBusy />,
          color: 'warning',
          navigationPath: navigationPaths.staff.list,
        });

        cards.push({
          id: 'staff-attendance-rate',
          title: translateText('dashboard.people.summary.staffAttendanceRate'),
          value: `${staffDashboardData.summary.attendancePercentage || 0}%`,
          icon: <TrendingUp />,
          color: 'info',
          navigationPath: navigationPaths.staff.attendance,
        });
      }

      if (attendanceDashboardData?.summary) {
        cards.push({
          id: 'overall-attendance-rate',
          title: translateText('dashboard.people.summary.overallAttendanceRate'),
          value: `${attendanceDashboardData.summary.totalPercentage || 0}%`,
          icon: <CheckCircle />,
          color: 'success',
          navigationPath: navigationPaths.students.attendance,
          subtitle: `${attendanceDashboardData.summary.totalCount || 0} ${translateText('dashboard.people.summary.present')}`,
          isCritical: true,
        });
      }
    }

    return cards;
  }, [
    peopleData,
    studentsDashboardData,
    staffDashboardData,
    attendanceDashboardData,
    translateText,
    navigationPaths,
  ]);

  const handleTimeRangeChange = (newRange: ITimeRangeParams) => {
    setTimeRange(newRange);
    // React Query will automatically refetch when timeRange changes (query key includes params)
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={400}
        data-testid="dashboard-people-tab-loading"
      >
        <Typography variant="body2" color="textSecondary">
          {translateText('common.loading')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        pb: { xs: 2, sm: 4 },
        px: { xs: 1, sm: 2 },
      }}
      data-testid="dashboard-people-tab-container"
    >
      {/* Summary Section - Hero Section (First) */}
      {summaryCards.length > 0 && (
        <Box mb={4}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
            flexWrap="wrap"
            gap={2}
          >
            <Typography variant="h6" fontWeight={600}>
              {translateText('dashboard.people.summary.title')}
            </Typography>
            <TimeRangePicker
              value={timeRange}
              onChange={handleTimeRangeChange}
              dataTestId="dashboard-people-time-range"
              showCustomRange={true}
            />
          </Box>
          <SummaryCards
            cards={summaryCards}
            columns={{ xs: 12, sm: 6, md: 3 }}
            dataTestId="dashboard-people-summary-cards"
            maxMobileCards={3}
          />
        </Box>
      )}

      {/* Action Items, Upcoming Items, and Recent Activity - Side by Side (Non-Mobile) */}
      {!isMobile ? (
        <Grid container spacing={3} mb={3}>
          {/* Action Items Column */}
          <Grid item xs={12} md={4}>
            {actionItems.length > 0 ? (
              <ActionItems
                title={translateText('dashboard.people.actionItems.title')}
                items={actionItems}
                isLoading={isLoading}
                emptyMessage={translateText('dashboard.people.actionItems.empty')}
                dataTestId="dashboard-people-action-items"
              />
            ) : (
              <Card
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <CardContent>
                  <Typography variant="body2" color="textSecondary" textAlign="center">
                    {translateText('dashboard.people.actionItems.empty')}
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Grid>

          {/* Upcoming Items Column */}
          <Grid item xs={12} md={4}>
            {upcomingItems.length > 0 ? (
              <UpcomingItems
                title={translateText('dashboard.people.upcomingItems.title')}
                items={upcomingItems}
                isLoading={isLoading}
                emptyMessage={translateText('dashboard.people.upcomingItems.empty')}
                dataTestId="dashboard-people-upcoming-items"
                maxItems={5}
              />
            ) : (
              <Card
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <CardContent>
                  <Typography variant="body2" color="textSecondary" textAlign="center">
                    {translateText('dashboard.people.upcomingItems.empty')}
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Grid>

          {/* Recent Activity Column */}
          <Grid item xs={12} md={4}>
            {peopleData?.recentActivity && peopleData.recentActivity.length > 0 ? (
              <Card
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  height: '100%',
                }}
                data-testid="dashboard-people-recent-activity"
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Typography variant="h6" fontWeight={600} mb={2}>
                    {translateText('dashboard.people.recentActivity.title')}
                  </Typography>
                  <Box
                    sx={{
                      maxHeight: 400,
                      overflowY: 'auto',
                    }}
                  >
                    {peopleData.recentActivity.map((activity) => (
                      <Box
                        key={activity.id}
                        onClick={() => {
                          if (activity.navigationPath) {
                            navigate(activity.navigationPath);
                          }
                        }}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 1,
                          px: 1,
                          borderBottom: `1px solid ${theme.palette.divider}`,
                          cursor: activity.navigationPath ? 'pointer' : 'default',
                          '&:hover': activity.navigationPath
                            ? {
                                backgroundColor: theme.palette.action.hover,
                                borderRadius: 1,
                              }
                            : {},
                          '&:last-child': {
                            borderBottom: 'none',
                          },
                        }}
                        data-testid={`dashboard-people-recent-activity-${activity.id}`}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2">{activity.description}</Typography>
                          {activity.relatedEntityName && (
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                              {activity.relatedEntityName}
                            </Typography>
                          )}
                        </Box>
                        <Typography variant="caption" color="textSecondary" sx={{ ml: 2 }}>
                          {new Date(activity.timestamp).toLocaleDateString()}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            ) : (
              <Card
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <CardContent>
                  <Typography variant="body2" color="textSecondary" textAlign="center">
                    {translateText('dashboard.people.recentActivity.empty')}
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      ) : (
        /* Mobile View - Stacked Layout */
        <>
          {/* Action Items Section - Mobile */}
          {actionItems.length > 0 && (
            <Box mb={3}>
              <ActionItems
                title={translateText('dashboard.people.actionItems.title')}
                items={actionItems}
                isLoading={isLoading}
                emptyMessage={translateText('dashboard.people.actionItems.empty')}
                dataTestId="dashboard-people-action-items"
              />
            </Box>
          )}

          {/* Upcoming Items Section - Mobile */}
          {upcomingItems.length > 0 && (
            <Box mb={3}>
              <UpcomingItems
                title={translateText('dashboard.people.upcomingItems.title')}
                items={upcomingItems}
                isLoading={isLoading}
                emptyMessage={translateText('dashboard.people.upcomingItems.empty')}
                dataTestId="dashboard-people-upcoming-items"
                maxItems={5}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default DashboardPeopleTabView;
